import React, { useEffect, useState } from "react";
import api from "../lib/api";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import { Button } from "./ui/button";
import { Plus, Trash2, X } from "lucide-react";
import { toast } from "sonner";

const DAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

export default function ClientSettingsForm({ client, onSaved, onCancel, isAdmin, onArchive }) {
  const [name, setName] = useState(client.name || "");
  const [overview, setOverview] = useState(client.overview || "");
  const [headerLogo, setHeaderLogo] = useState(client.header_logo_url || "");
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [retainerInt, setRetainerInt] = useState(client.retainer_interaction_days ?? 2);
  const [retainerWork, setRetainerWork] = useState(client.retainer_work_days ?? 5);
  const [assignedTeams, setAssignedTeams] = useState(client.assigned_team_ids || []);
  const [checkInFields, setCheckInFields] = useState(client.check_in_fields || []);
  const [payments, setPayments] = useState(client.payments || []);
  const [contractValue, setContractValue] = useState(client.contract_value ?? "");
  const [engagementStart, setEngagementStart] = useState(client.engagement_start || "");
  const [engagementEnd, setEngagementEnd] = useState(client.engagement_end || "");
  const [happiness, setHappiness] = useState(client.happiness_form || { rating_enabled: true, notes_enabled: true, dropdowns: [] });
  const [schedule, setSchedule] = useState(client.happiness_schedule || { day_of_week: null, hour: null, closeable: false });
  const [teams, setTeams] = useState([]);
  const [busy, setBusy] = useState(false);
  // Meta Ads integration
  const [metaToken, setMetaToken] = useState(client.meta_ads?.access_token || "");
  const [metaAdAccount, setMetaAdAccount] = useState(client.meta_ads?.ad_account_id || "");
  const [metaEnabled, setMetaEnabled] = useState(!!client.meta_ads?.enabled);
  const [metaTesting, setMetaTesting] = useState(false);
  const [metaTestResult, setMetaTestResult] = useState(null);
  const [metaAccounts, setMetaAccounts] = useState([]);
  const [metaLoadingAccounts, setMetaLoadingAccounts] = useState(false);
  const [resourcesEnabled, setResourcesEnabled] = useState(!!client.resources_enabled);

  useEffect(() => { api.get("/teams").then((r) => setTeams(r.data || [])).catch(() => {}); }, []);

  const toggleTeam = (tid) => setAssignedTeams(assignedTeams.includes(tid) ? assignedTeams.filter((x) => x !== tid) : [...assignedTeams, tid]);

  const onLogoFile = async (file) => {
    if (!file) return;
    setUploadingLogo(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await api.post("/files/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      const url = `${(process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "")}/api/files/${r.data.id}`;
      setHeaderLogo(url);
    } catch (e) { toast.error("Upload failed"); }
    finally { setUploadingLogo(false); }
  };

  const addCheckInField = () => setCheckInFields([...checkInFields, { id: crypto.randomUUID(), label: "New check-in question", type: "text" }]);
  const updateCheckIn = (idx, patch) => setCheckInFields(checkInFields.map((f, i) => i === idx ? { ...f, ...patch } : f));
  const removeCheckIn = (idx) => setCheckInFields(checkInFields.filter((_, i) => i !== idx));

  const addPayment = () => setPayments([...payments, {
    id: crypto.randomUUID(),
    date: new Date().toISOString().slice(0, 10),
    amount: 0, tax_percent: 18, paid: false, note: "",
  }]);
  const updatePayment = (idx, patch) => setPayments(payments.map((p, i) => i === idx ? { ...p, ...patch } : p));
  const removePayment = (idx) => setPayments(payments.filter((_, i) => i !== idx));
  const totalOutstanding = payments
    .filter((p) => !p.paid)
    .reduce((s, p) => s + Number(p.amount || 0) * (1 + Number(p.tax_percent || 0) / 100), 0);

  const addDropdown = () => {
    if ((happiness.dropdowns || []).length >= 2) { toast.error("Max 2 custom dropdowns"); return; }
    setHappiness({ ...happiness, dropdowns: [...(happiness.dropdowns || []), { id: crypto.randomUUID(), label: "New question", options: [] }] });
  };
  const updateDropdown = (idx, patch) => setHappiness({
    ...happiness,
    dropdowns: happiness.dropdowns.map((d, i) => i === idx ? { ...d, ...patch } : d),
  });
  const removeDropdown = (idx) => setHappiness({ ...happiness, dropdowns: happiness.dropdowns.filter((_, i) => i !== idx) });

  const save = async () => {
    setBusy(true);
    try {
      const body = {
        name, overview, header_logo_url: headerLogo || null,
        retainer_interaction_days: retainerInt, retainer_work_days: retainerWork,
        assigned_team_ids: assignedTeams, check_in_fields: checkInFields,
        happiness_form: happiness, happiness_schedule: schedule,
        meta_ads: {
          access_token: metaToken || "",
          ad_account_id: metaAdAccount || "",
          enabled: metaEnabled,
        },
        resources_enabled: resourcesEnabled,
      };
      if (isAdmin) {
        body.payments = payments;
        body.contract_value = contractValue === "" ? null : Number(contractValue);
        body.engagement_start = engagementStart || null;
        body.engagement_end = engagementEnd || null;
      }
      await api.put(`/clients/${client.id}`, body);
      toast.success("Settings saved");
      onSaved();
    } catch (e) { toast.error("Save failed"); }
    finally { setBusy(false); }
  };

  const testMetaToken = async () => {
    // We need to save first if token differs, otherwise test against stored value
    setMetaTesting(true); setMetaTestResult(null);
    try {
      // Save token first so backend can use it for testing
      await api.put(`/clients/${client.id}`, {
        meta_ads: { access_token: metaToken, ad_account_id: metaAdAccount || "", enabled: metaEnabled },
      });
      const r = await api.post(`/clients/${client.id}/meta_ads/test`);
      setMetaTestResult(r.data);
      if (r.data?.ok) {
        // Fetch ad accounts so the admin can pick one
        try {
          setMetaLoadingAccounts(true);
          const a = await api.get(`/clients/${client.id}/meta_ads/accounts`);
          setMetaAccounts(a.data || []);
        } catch (e) {
          toast.error(e?.response?.data?.detail || "Failed to list ad accounts");
        } finally { setMetaLoadingAccounts(false); }
      }
    } catch (e) {
      setMetaTestResult({ ok: false, error_message: e?.response?.data?.detail || "Failed to test token" });
    } finally { setMetaTesting(false); }
  };

  return (
    <div className="space-y-5 mt-1" data-testid="client-settings-form">
      {/* Basics */}
      <div className="space-y-2">
        <Input value={name} onChange={(e) => setName(e.target.value)} className="bg-[#0a0a0a] border-zinc-800 text-[20px] font-display font-semibold h-10" placeholder="Client name" data-testid="settings-name" />
        <Textarea value={overview} onChange={(e) => setOverview(e.target.value)} rows={2} className="bg-[#0a0a0a] border-zinc-800 text-[13px]" placeholder="Overview" />

        {/* Header logo (white/light PNG shown in top bar in place of client name) */}
        <div className="bg-[#0a0a0a] border border-zinc-800 rounded-md p-3" data-testid="settings-header-logo">
          <div className="t-eyebrow mb-2">Header logo · top-bar (white/light PNG)</div>
          <div className="flex items-center gap-3">
            <div className="h-10 min-w-[80px] max-w-[200px] rounded bg-[#111] border border-zinc-900 flex items-center justify-center px-3 overflow-hidden">
              {headerLogo ? (
                <img src={headerLogo} alt="" className="h-7 object-contain" />
              ) : (
                <span className="text-[10px] text-zinc-700 font-mono uppercase tracking-wider">no logo</span>
              )}
            </div>
            <label className="text-[11px] text-zinc-400 cursor-pointer px-3 h-8 border border-zinc-800 rounded flex items-center hover:border-zinc-600">
              {uploadingLogo ? "Uploading…" : "Upload PNG"}
              <input type="file" accept="image/png,image/svg+xml,image/webp" className="hidden"
                onChange={(e) => onLogoFile(e.target.files?.[0])} data-testid="settings-header-logo-input" />
            </label>
            {headerLogo && (
              <button type="button" onClick={() => setHeaderLogo("")} className="text-[11px] text-zinc-500 hover:text-[#EF4444]">Clear</button>
            )}
          </div>
          <p className="text-[10px] text-zinc-600 mt-2">When set, this replaces the client's name in the top bar. Falls back to the name if not set.</p>
        </div>

        <div className="grid grid-cols-2 gap-2">
          <label className="text-[10px] uppercase tracking-[0.18em] text-zinc-500 flex flex-col gap-1">
            Interaction red-flag (days)
            <Input type="number" min={1} value={retainerInt} onChange={(e) => setRetainerInt(parseInt(e.target.value || "2"))} className="bg-[#0a0a0a] border-zinc-800 text-[12px] h-8 font-mono normal-case tracking-normal" />
          </label>
          <label className="text-[10px] uppercase tracking-[0.18em] text-zinc-500 flex flex-col gap-1">
            Delivery red-flag (days)
            <Input type="number" min={1} value={retainerWork} onChange={(e) => setRetainerWork(parseInt(e.target.value || "5"))} className="bg-[#0a0a0a] border-zinc-800 text-[12px] h-8 font-mono normal-case tracking-normal" />
          </label>
        </div>
      </div>

      {/* Assigned teams */}
      <div className="tt-card p-3.5">
        <div className="t-eyebrow mb-2">Assigned team(s)</div>
        <p className="text-[11px] text-zinc-600 mb-2">All members of the selected team(s) will see this client in their team workspace.</p>
        <div className="flex flex-wrap gap-1.5" data-testid="settings-assigned-teams">
          {teams.length === 0 ? (
            <span className="text-[11px] text-zinc-600">No teams yet. Create one in Accounts → Teams.</span>
          ) : teams.map((t) => {
            const on = assignedTeams.includes(t.id);
            return (
              <button key={t.id} type="button" onClick={() => toggleTeam(t.id)}
                data-testid={`assign-team-${t.id}`}
                className={`px-2.5 h-8 rounded text-[12px] tracking-wide border transition-all ${
                  on ? "bg-white text-black border-white" : "bg-transparent text-zinc-400 border-zinc-800 hover:border-zinc-600"
                }`}>
                {t.name} <span className="text-[10px] opacity-60 ml-1 font-mono">{(t.member_user_ids || []).length}</span>
              </button>
            );
          })}
        </div>
      </div>

      {/* Resources tab toggle */}
      <div className="tt-card p-3.5" data-testid="settings-resources-card">
        <div className="flex items-center justify-between mb-1">
          <div className="t-eyebrow">Resources tab</div>
          <label className="text-[11px] text-zinc-300 flex items-center gap-1.5 cursor-pointer">
            <input type="checkbox" checked={resourcesEnabled}
              onChange={(e) => setResourcesEnabled(e.target.checked)}
              data-testid="resources-enabled-toggle" />
            <span>{resourcesEnabled ? "Visible to client" : "Hidden from client"}</span>
          </label>
        </div>
        <p className="text-[11px] text-zinc-600">
          When on, this client sees a "Resources" link on their dashboard for shared PDFs, decks, links and embeds. Staff can always access it to prepare content.
        </p>
      </div>

      {/* Custom check-in fields */}
      <div className="tt-card p-3.5">
        <div className="flex items-center justify-between mb-2">
          <div className="t-eyebrow">Custom check-in fields</div>
          <Button size="sm" variant="ghost" onClick={addCheckInField} className="text-zinc-300 text-[11px] h-7" data-testid="settings-add-checkin"><Plus className="w-3 h-3 mr-1" /> Add</Button>
        </div>
        {checkInFields.length === 0 && <div className="text-[11px] text-zinc-600">No check-ins configured yet.</div>}
        <ul className="space-y-2">
          {checkInFields.map((f, i) => {
            const rec = f.recurrence || "daily";
            return (
              <li key={f.id} className="tt-card p-2.5 bg-[#0a0a0a] space-y-2" data-testid={`checkin-field-row-${f.id}`}>
                <div className="flex items-center gap-2">
                  <Input value={f.label} onChange={(e) => updateCheckIn(i, { label: e.target.value })} className="bg-[#0a0a0a] border-zinc-800 h-8 text-[12px] flex-1" />
                  <select value={f.type} onChange={(e) => updateCheckIn(i, { type: e.target.value })}
                    className="bg-[#111] border border-zinc-800 rounded h-8 text-[11px] px-2">
                    <option value="text">text</option>
                    <option value="checkbox">checkbox</option>
                    <option value="number">number</option>
                    <option value="ticket_sales">ticket sales 🎟</option>
                    <option value="roas">ROAS today 📈</option>
                  </select>
                  <Button size="sm" variant="ghost" onClick={() => removeCheckIn(i)} className="h-8 w-8 p-0 text-zinc-500 hover:text-[#EF4444]"><Trash2 className="w-3 h-3" /></Button>
                </div>
                <div className="flex items-center gap-2 flex-wrap text-[11px]">
                  <span className="text-[10px] uppercase tracking-[0.18em] text-zinc-500 mr-1">Repeats</span>
                  <select value={rec} onChange={(e) => {
                    const newRec = e.target.value;
                    const patch = { recurrence: newRec };
                    if (newRec === "weekly" && f.weekday == null) patch.weekday = 0;
                    if (newRec === "custom_repeat" && !f.interval_days) patch.interval_days = 7;
                    if (newRec === "custom_date" && !f.due_date) patch.due_date = new Date().toISOString().slice(0, 10);
                    updateCheckIn(i, patch);
                  }} className="bg-[#111] border border-zinc-800 rounded h-7 text-[11px] px-2" data-testid={`recurrence-${f.id}`}>
                    <option value="daily">Daily</option>
                    <option value="weekly">Weekly</option>
                    <option value="custom_repeat">Every N days</option>
                    <option value="custom_date">One-off date</option>
                  </select>
                  {rec === "weekly" && (
                    <select value={String(f.weekday ?? 0)} onChange={(e) => updateCheckIn(i, { weekday: parseInt(e.target.value, 10) })}
                      className="bg-[#111] border border-zinc-800 rounded h-7 text-[11px] px-2">
                      {["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"].map((d, idx) => (
                        <option key={d} value={idx}>{d}</option>
                      ))}
                    </select>
                  )}
                  {rec === "custom_repeat" && (
                    <div className="flex items-center gap-1">
                      <span className="text-zinc-500">every</span>
                      <Input type="number" min={1} value={f.interval_days ?? 7}
                        onChange={(e) => updateCheckIn(i, { interval_days: Math.max(1, parseInt(e.target.value || "1", 10)) })}
                        className="bg-[#0a0a0a] border-zinc-800 h-7 text-[11px] w-16" />
                      <span className="text-zinc-500">days</span>
                    </div>
                  )}
                  {rec === "custom_date" && (
                    <Input type="date" value={f.due_date || ""}
                      onChange={(e) => updateCheckIn(i, { due_date: e.target.value })}
                      className="bg-[#0a0a0a] border-zinc-800 h-7 text-[11px] w-40" />
                  )}
                </div>
              </li>
            );
          })}
        </ul>
      </div>

      {/* Contract & engagement (admin only) */}
      {isAdmin && (
        <div className="tt-card p-3.5" data-testid="settings-engagement-card">
          <div className="t-eyebrow mb-2">Contract &amp; engagement</div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
            <div>
              <Label className="text-[10px] uppercase tracking-wider text-zinc-500 font-mono">Overall value (₹)</Label>
              <Input type="number" step="0.01" value={contractValue}
                onChange={(e) => setContractValue(e.target.value)}
                className="bg-[#0a0a0a] border-zinc-800 h-8 text-[12px] mt-1"
                data-testid="settings-contract-value" />
            </div>
            <div>
              <Label className="text-[10px] uppercase tracking-wider text-zinc-500 font-mono">Engagement start</Label>
              <Input type="date" value={engagementStart}
                onChange={(e) => setEngagementStart(e.target.value)}
                className="bg-[#0a0a0a] border-zinc-800 h-8 text-[12px] mt-1"
                data-testid="settings-engagement-start" />
            </div>
            <div>
              <Label className="text-[10px] uppercase tracking-wider text-zinc-500 font-mono">Engagement end</Label>
              <Input type="date" value={engagementEnd}
                onChange={(e) => setEngagementEnd(e.target.value)}
                className="bg-[#0a0a0a] border-zinc-800 h-8 text-[12px] mt-1"
                data-testid="settings-engagement-end" />
            </div>
          </div>
        </div>
      )}

      {/* Payments (admin only) */}
      {isAdmin && (
        <div className="tt-card p-3.5" data-testid="settings-payments-card">
          <div className="flex items-center justify-between mb-2">
            <div className="t-eyebrow">Payments</div>
            <div className="flex items-center gap-3">
              <span className="text-[11px] text-zinc-500 font-mono" data-testid="payments-outstanding">
                Outstanding: ₹{totalOutstanding.toLocaleString("en-IN", { maximumFractionDigits: 2 })}
              </span>
              <Button size="sm" variant="ghost" onClick={addPayment} className="text-zinc-300 text-[11px] h-7" data-testid="settings-add-payment"><Plus className="w-3 h-3 mr-1" /> Add</Button>
            </div>
          </div>
          {payments.length === 0 && <div className="text-[11px] text-zinc-600">No payments recorded yet.</div>}
          <ul className="space-y-1.5">
            {payments.map((p, i) => {
              const incl = Number(p.amount || 0) * (1 + Number(p.tax_percent || 0) / 100);
              return (
                <li key={p.id} className="grid grid-cols-12 gap-2 items-center" data-testid={`payment-row-${p.id}`}>
                  <Input type="date" value={p.date || ""} onChange={(e) => updatePayment(i, { date: e.target.value })}
                    className="col-span-3 bg-[#0a0a0a] border-zinc-800 h-8 text-[12px]" />
                  <Input type="number" step="0.01" placeholder="Amount"
                    value={p.amount ?? ""} onChange={(e) => updatePayment(i, { amount: parseFloat(e.target.value) || 0 })}
                    className="col-span-2 bg-[#0a0a0a] border-zinc-800 h-8 text-[12px]" />
                  <div className="col-span-2 flex items-center gap-1">
                    <Input type="number" step="0.01" placeholder="Tax %"
                      value={p.tax_percent ?? ""} onChange={(e) => updatePayment(i, { tax_percent: parseFloat(e.target.value) || 0 })}
                      className="bg-[#0a0a0a] border-zinc-800 h-8 text-[12px]" />
                    <span className="text-[10px] text-zinc-600">%</span>
                  </div>
                  <Input value={p.note || ""} onChange={(e) => updatePayment(i, { note: e.target.value })}
                    placeholder="Note"
                    className="col-span-3 bg-[#0a0a0a] border-zinc-800 h-8 text-[12px]" />
                  <label className="col-span-1 flex items-center gap-1.5 text-[11px] text-zinc-400">
                    <input type="checkbox" checked={!!p.paid} onChange={(e) => updatePayment(i, { paid: e.target.checked })}
                      data-testid={`payment-paid-${p.id}`} />
                    Paid
                  </label>
                  <div className="col-span-1 flex items-center justify-end gap-1">
                    <span className="text-[10px] text-zinc-500 font-mono" title="Incl. tax">₹{incl.toLocaleString("en-IN", { maximumFractionDigits: 0 })}</span>
                    <Button size="sm" variant="ghost" onClick={() => removePayment(i)} className="h-8 w-8 p-0 text-zinc-500 hover:text-[#EF4444]"><Trash2 className="w-3 h-3" /></Button>
                  </div>
                </li>
              );
            })}
          </ul>
        </div>
      )}

      {/* Happiness form */}
      <div className="tt-card p-3.5">
        <div className="flex items-center justify-between mb-2">
          <div className="t-eyebrow">Weekly happiness form</div>
          <label className="flex items-center gap-1.5 text-[11px] text-zinc-300">
            <input type="checkbox" checked={happiness.enabled !== false}
              onChange={(e) => setHappiness({ ...happiness, enabled: e.target.checked })}
              data-testid="happiness-enabled-toggle" />
            <span>{happiness.enabled !== false ? "Enabled · pops up weekly" : "Disabled · never pops up"}</span>
          </label>
        </div>
        <div className={happiness.enabled === false ? "opacity-50 pointer-events-none" : ""}>
        <div className="flex gap-3 text-[11px] text-zinc-400 mb-3">
          <label className="flex items-center gap-1.5">
            <input type="checkbox" checked={!!happiness.rating_enabled} onChange={(e) => setHappiness({ ...happiness, rating_enabled: e.target.checked })} /> 1–10 rating
          </label>
          <label className="flex items-center gap-1.5">
            <input type="checkbox" checked={!!happiness.notes_enabled} onChange={(e) => setHappiness({ ...happiness, notes_enabled: e.target.checked })} /> Notes textarea
          </label>
        </div>
        {(happiness.dropdowns || []).map((d, i) => (
          <div key={d.id} className="bg-[#0a0a0a] border border-zinc-800 rounded-md p-2.5 space-y-2 mb-2">
            <div className="flex items-center gap-2">
              <Input value={d.label} onChange={(e) => updateDropdown(i, { label: e.target.value })} placeholder="Question" className="bg-transparent border-zinc-800 h-8 text-[12px] flex-1" />
              <Button size="sm" variant="ghost" onClick={() => removeDropdown(i)} className="h-7 w-7 p-0 text-zinc-500 hover:text-[#EF4444]"><X className="w-3 h-3" /></Button>
            </div>
            <Input value={(d.options || []).join(", ")} onChange={(e) => updateDropdown(i, { options: e.target.value.split(",").map((s) => s.trim()).filter(Boolean) })}
              placeholder="Comma-separated options"
              className="bg-transparent border-zinc-800 h-8 text-[12px]" />
          </div>
        ))}
        {(happiness.dropdowns || []).length < 2 && (
          <Button size="sm" variant="ghost" onClick={addDropdown} className="text-zinc-300 text-[11px] h-7" data-testid="settings-add-dropdown"><Plus className="w-3 h-3 mr-1" /> Add custom dropdown</Button>
        )}

        <div className="grid grid-cols-3 gap-2 mt-3">
          <label className="text-[10px] uppercase tracking-[0.18em] text-zinc-500 flex flex-col gap-1">
            Day
            <select value={schedule.day_of_week ?? ""} onChange={(e) => setSchedule({ ...schedule, day_of_week: e.target.value === "" ? null : parseInt(e.target.value) })}
              className="bg-[#0a0a0a] border border-zinc-800 rounded h-8 text-[12px] px-2 normal-case tracking-normal">
              <option value="">Random</option>
              {DAYS.map((d, idx) => <option key={d} value={idx}>{d}</option>)}
            </select>
          </label>
          <label className="text-[10px] uppercase tracking-[0.18em] text-zinc-500 flex flex-col gap-1">
            Hour (IST)
            <Input type="number" min={0} max={23} value={schedule.hour ?? ""} onChange={(e) => setSchedule({ ...schedule, hour: e.target.value === "" ? null : parseInt(e.target.value) })} className="bg-[#0a0a0a] border-zinc-800 text-[12px] h-8 font-mono normal-case tracking-normal" placeholder="Random" />
          </label>
          <label className="text-[10px] uppercase tracking-[0.18em] text-zinc-500 flex items-center gap-2 mt-5">
            <input type="checkbox" checked={!!schedule.closeable} onChange={(e) => setSchedule({ ...schedule, closeable: e.target.checked })} /> Closeable
          </label>
        </div>
        </div>
      </div>

      {/* Meta Ads integration */}
      <div className="tt-card p-3.5" data-testid="settings-meta-ads">
        <div className="flex items-center justify-between mb-2">
          <div className="t-eyebrow">Meta Ads integration</div>
          <label className="text-[11px] text-zinc-400 flex items-center gap-1.5">
            <input type="checkbox" checked={metaEnabled} onChange={(e) => setMetaEnabled(e.target.checked)} data-testid="meta-ads-enabled" />
            Enabled
          </label>
        </div>
        <p className="text-[11px] text-zinc-600 mb-2">Paste a long-lived Meta access token (System User token recommended — valid ~60 days). The token is stored on the client document and never exposed to the client role.</p>
        <Input
          type="password" value={metaToken} onChange={(e) => setMetaToken(e.target.value)}
          placeholder="EAA... (paste long-lived access token)"
          className="bg-[#0a0a0a] border-zinc-800 text-[12px] h-9 font-mono normal-case tracking-normal"
          data-testid="meta-ads-token-input" />
        <div className="flex gap-2 mt-2 flex-wrap">
          <Button size="sm" onClick={testMetaToken} disabled={!metaToken || metaTesting}
            className="bg-white text-black hover:bg-zinc-200 h-8 text-[11px]" data-testid="meta-ads-test-btn">
            {metaTesting ? "Testing…" : "Test & list ad accounts"}
          </Button>
          {metaTestResult && (
            <div className={`text-[11px] flex items-center px-2 ${metaTestResult.ok ? "text-emerald-300" : "text-[#FCA5A5]"}`} data-testid="meta-ads-test-result">
              {metaTestResult.ok ? `OK · ${metaTestResult.name}` : `Error: ${metaTestResult.error_message}`}
            </div>
          )}
        </div>
        {(metaAccounts.length > 0 || metaAdAccount) && (
          <div className="mt-3">
            <Label className="text-[10px] uppercase tracking-[0.18em] text-zinc-500">Ad account</Label>
            {metaAccounts.length > 0 ? (
              <select value={metaAdAccount} onChange={(e) => setMetaAdAccount(e.target.value)}
                className="w-full bg-[#0a0a0a] border border-zinc-800 rounded h-8 mt-1 text-[12px] px-2"
                data-testid="meta-ads-account-select">
                <option value="">Select ad account…</option>
                {metaAccounts.map((a) => (
                  <option key={a.account_id || a.id} value={a.account_id ? `act_${a.account_id}` : a.id}>
                    {a.name} ({a.account_id || a.id})
                  </option>
                ))}
              </select>
            ) : (
              <Input value={metaAdAccount} onChange={(e) => setMetaAdAccount(e.target.value)}
                placeholder="act_1234567890"
                className="bg-[#0a0a0a] border-zinc-800 text-[12px] h-8 font-mono normal-case tracking-normal mt-1" />
            )}
            <p className="text-[10px] text-zinc-600 mt-1.5">{metaLoadingAccounts ? "Loading…" : "Pick which ad account this client's Meta Ads block will pull from."}</p>
          </div>
        )}
      </div>

      {/* Actions */}
      <div className="flex gap-2 items-center pt-1">
        <Button size="sm" onClick={save} disabled={busy} className="bg-white hover:bg-zinc-200 text-black text-[12px] h-8" data-testid="settings-save">Save settings</Button>
        <Button size="sm" variant="ghost" onClick={onCancel} className="text-zinc-500 text-[12px] h-8">Cancel</Button>
        <div className="flex-1" />
        {isAdmin && (
          <Button size="sm" variant="ghost" onClick={onArchive}
            className="text-zinc-500 hover:text-[#EF4444] text-[12px] h-8" data-testid="delete-client-btn">
            <Trash2 className="w-3.5 h-3.5 mr-1" /> Archive client
          </Button>
        )}
      </div>
    </div>
  );
}
